import { useEffect, useState } from "react";

import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import { makeStyles } from "@mui/styles";
import { useIntl } from "react-intl";

type LanguageOption = {
  value: string;
  id: string;
  primaryText: string;
};

const options: LanguageOption[] = [
  {
    value: "en-US",
    id: "lang-EN",
    primaryText: "EN",
  },
  {
    value: "cs-CZ",
    id: "lang-CZ",
    primaryText: "CZ",
  },
  {
    value: "es-ES",
    id: "lang-ES",
    primaryText: "ES",
  },
  {
    value: "ro-RO",
    id: "lang-RO",
    primaryText: "RO",
  },
  {
    value: "hu-HU",
    id: "lang-HU",
    primaryText: "HU",
  },
  {
    value: "pl-PL",
    id: "lang-PL",
    primaryText: "PL",
  },
  {
    value: "sk-SK",
    id: "lang-SK",
    primaryText: "SK",
  },
  {
    value: "lt-LT",
    id: "lang-LT",
    primaryText: "LT",
  },
];

const useStyles = makeStyles(() => ({
  menuItemChild: {
    width: 45,
    textAlign: "center" as const,
  },
}));

type LangSwitchProps = {
  onLangChange?: (locale: string) => void;
  className?: string;
};

const LangSwitch = (props: LangSwitchProps): JSX.Element => {
  const { onLangChange = () => {}, className = "" } = props;
  const classes = useStyles();
  const intl = useIntl();

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [open, setOpen] = useState<boolean>(false);
  const [selectedIndex, setSelectedIndex] = useState<number>(() => {
    const index = options.map((o) => o.value).indexOf(intl.locale);
    return index !== -1 ? index : 0;
  });

  useEffect(() => {
    const currentSelectedLocale = options[selectedIndex]?.value;
    if (currentSelectedLocale !== intl.locale) {
      const newIndex = options.findIndex(
        (option) => option.value === intl.locale,
      );
      if (newIndex !== -1 && newIndex !== selectedIndex) {
        setSelectedIndex(newIndex);
      }
    }
  }, [intl.locale]);

  const handleClickListItem = (event: React.MouseEvent<HTMLElement>) => {
    setOpen(true);
    setAnchorEl(event.currentTarget);
  };

  const handleMenuItemClick = (
    _event: React.MouseEvent<HTMLElement>,
    index: number,
  ) => {
    setSelectedIndex(index);
    setOpen(false);
    onLangChange(options[index].value);
  };

  const handleRequestClose = () => {
    setOpen(false);
  };

  return (
    <div className={className} id="lang-switch">
      <List>
        <ListItem
          aria-controls="lock-menu"
          aria-haspopup="true"
          button
          onClick={handleClickListItem}
        >
          {/* eslint-disable-next-line formatjs/no-literal-string-in-jsx */}
          <span id="lang-code">{options[selectedIndex].primaryText}</span>{" "}
          {/* eslint-disable-next-line formatjs/no-literal-string-in-jsx */}
          <KeyboardArrowDownIcon />
        </ListItem>
      </List>
      <Menu anchorEl={anchorEl} onClose={handleRequestClose} open={open}>
        {options.map((option, index) => (
          <MenuItem
            id={`lang-${option.primaryText}`}
            key={option.value}
            onClick={(event) => handleMenuItemClick(event, index)}
            selected={index === selectedIndex}
            value={option.value}
          >
            {/* eslint-disable-next-line formatjs/no-literal-string-in-jsx */}
            <span className={classes.menuItemChild}>{option.primaryText}</span>
          </MenuItem>
        ))}
      </Menu>
    </div>
  );
};

export { LangSwitch };
export { options };
